/**
 * togu. — Tiered Product Exclusivity for Cart (REACTIVE VERSION)
 * -----------------------------------------------------------------
 * This theme uses a compiled Alpine.js "islands" framework for the
 * add-to-cart flow (island-product.bundle.js) — not a simple fetch call
 * we can safely intercept in Liquid. Patching a compiled bundle is fragile
 * (gets overwritten on theme updates), so instead this script reacts
 * AFTER an add-to-cart happens: it watches for form submits, then polls
 * the cart briefly until the new item shows up, and cleans up any lower
 * tier from the same family — showing a small toast so the customer knows
 * what happened.
 *
 * REQUIRED THEME EDIT (one line, in snippets/product-form-component-buy-buttons.liquid):
 *   Add `data-product-handle: product.handle` to the `{% form 'product', ... %}` tag
 *   so this script knows which product handle is being submitted.
 *
 * HOW MATCHING WORKS:
 * Each cart line item exposes `product_handle` (from /cart.js) — the
 * Shopify product handle/slug. Update TOGU_TIER_GROUPS below to match
 * your ACTUAL Shopify handles (Shopify Admin → Products → URL slug).
 */

// ---------------------------------------------------------------------
// CONFIG — edit this to match real Shopify product handles
// Order matters: index 0 = lowest tier, last index = highest tier.
// The "label" is what's shown in the toast message.
// ---------------------------------------------------------------------
const TOGU_TIER_GROUPS = [
  // Care and Pro-Service groups are intentionally disabled for now:
  // repair/thin/restore run through the post-purchase knife-check-in flow,
  // not the normal product-page add-to-cart form this script listens to.
  // Re-enable + fill in real handles once that flow is built and it's clear
  // how those items actually enter the cart.
  //
  // {
  //   name: 'care',
  //   handles: [
  //     { handle: 'togu-care-plus', label: 'togu.care+' },
  //     { handle: 'togu-care-plus-plus', label: 'togu.care++' },
  //   ],
  // },
  {
    name: 'packages',
    handles: [
      { handle: 'togu-03', label: 'togu.03' },
      { handle: 'togu-05', label: 'togu.05' },
      { handle: 'togu-07', label: 'togu.07' },
    ],
  },
  // {
  //   name: 'proservice',
  //   handles: [
  //     { handle: 'togu-repair', label: 'togu.repair' },
  //     { handle: 'togu-thin', label: 'togu.thin' },
  //     { handle: 'togu-restore', label: 'togu.restore' },
  //   ],
  // },
];

// togu.backup and togu.trio are intentionally NOT in any group above —
// they're independent and can co-exist with anything.

// ---------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------

function findGroupForHandle(handle) {
  for (const group of TOGU_TIER_GROUPS) {
    const rank = group.handles.findIndex((h) => h.handle === handle);
    if (rank !== -1) return { group, rank, label: group.handles[rank].label };
  }
  return null;
}

async function getCart() {
  const res = await fetch('/cart.js');
  if (!res.ok) throw new Error('Failed to fetch cart');
  return res.json();
}

/**
 * Removes one cart line item by key, requesting freshly rendered section
 * HTML (matching exactly what the theme's own quantity-change flow does),
 * then dispatches the theme's real `baseline:cart:cartqtychange` event so
 * the CartItems/CartFooter/CartCount Alpine components properly re-render.
 */
async function removeLineByKey(itemKey, previousQuantity, variantId) {
  const request = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: itemKey,
      quantity: 0,
      sections: 'cart-items,cart-footer,cart-item-count,cart-live-region',
      sections_url: window.location.pathname,
    }),
  };

  const res = await fetch('/cart/change.js', request);
  const data = await res.json();

  // This mirrors exactly what the theme's own CartItem component dispatches
  // after a successful quantity change, so CartItems/CartFooter/cart count
  // all re-render correctly via Alpine.morph with the returned section HTML.
  document.body.dispatchEvent(
    new CustomEvent('baseline:cart:cartqtychange', {
      bubbles: true,
      detail: {
        response: data,
        key: itemKey,
        quantity: 0,
        previousQuantity,
        variantId,
        originalTarget: null,
      },
    })
  );

  return data;
}

/**
 * Scans the whole cart and, for every tier group, keeps only the
 * highest-ranked item present, removing all lower-ranked ones in the
 * same group. Returns info about what was removed and what was kept,
 * for the toast message.
 */
async function enforceTierExclusivity() {
  const cart = await getCart();

  const highestPerGroup = {}; // group.name -> { rank, label }
  cart.items.forEach((item) => {
    const match = findGroupForHandle(item.product_handle || item.handle);
    if (!match) return;
    const current = highestPerGroup[match.group.name];
    if (!current || match.rank > current.rank) {
      highestPerGroup[match.group.name] = { rank: match.rank, label: match.label };
    }
  });

  const itemsToRemove = [];
  const removedLabels = [];

  cart.items.forEach((item) => {
    const match = findGroupForHandle(item.product_handle || item.handle);
    if (!match) return;
    const winner = highestPerGroup[match.group.name];
    if (match.rank < winner.rank) {
      itemsToRemove.push(item);
      removedLabels.push({ removed: match.label, keptInstead: winner.label });
    }
  });

  if (itemsToRemove.length === 0) return null;

  // Remove sequentially so each request operates on the current cart state
  for (const item of itemsToRemove) {
    await removeLineByKey(item.key, item.quantity, item.variant_id);
  }

  return removedLabels; // e.g. [{ removed: 'togu.03', keptInstead: 'togu.05' }]
}

/**
 * Polls /cart.js briefly after a submit to detect once the new item has
 * landed, then runs the exclusivity cleanup. Stops after ~4s if nothing
 * changes (safety net in case the add-to-cart failed).
 */
async function waitForCartChangeThenEnforce(previousItemCount) {
  const maxAttempts = 10;
  const delayMs = 400;

  console.log('[togu-tiered-cart] polling for cart change, starting count =', previousItemCount);

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await new Promise((r) => setTimeout(r, delayMs));
    try {
      const cart = await getCart();
      console.log(`[togu-tiered-cart] poll attempt ${attempt + 1}, item_count =`, cart.item_count);
      if (cart.item_count !== previousItemCount) {
        console.log('[togu-tiered-cart] cart change detected, running enforceTierExclusivity');
        const removedLabels = await enforceTierExclusivity();
        console.log('[togu-tiered-cart] enforceTierExclusivity result:', removedLabels);
        if (removedLabels && removedLabels.length > 0) {
          showTierSwapToast(removedLabels);
        }
        return;
      }
    } catch (e) {
      console.error('togu tiered-cart check failed', e);
      return;
    }
  }
  console.log('[togu-tiered-cart] gave up polling after', maxAttempts, 'attempts, no cart change detected');
}

// ---------------------------------------------------------------------
// Toast notification — small, dismisses itself, brand-styled
// ---------------------------------------------------------------------
function showTierSwapToast(removedLabels) {
  // Build message, e.g. "togu.03 wurde durch togu.05 ersetzt."
  const messages = removedLabels.map(
    ({ removed, keptInstead }) => `${removed} wurde durch ${keptInstead} ersetzt.`
  );

  const toast = document.createElement('div');
  toast.setAttribute('role', 'status');
  toast.style.cssText = `
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%) translateY(20px);
    background: #111111;
    color: #C8A882;
    font-family: 'Figtree', sans-serif;
    font-weight: 700;
    font-size: 14px;
    letter-spacing: -0.01em;
    padding: 14px 24px;
    z-index: 9999;
    opacity: 0;
    transition: opacity 0.25s ease, transform 0.25s ease;
    max-width: 90vw;
    text-align: center;
  `;
  toast.textContent = messages.join(' ');
  document.body.appendChild(toast);

  requestAnimationFrame(() => {
    toast.style.opacity = '1';
    toast.style.transform = 'translateX(-50%) translateY(0)';
  });

  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transform = 'translateX(-50%) translateY(20px)';
    setTimeout(() => toast.remove(), 300);
  }, 3500);
}

// ---------------------------------------------------------------------
// Global listener — attaches to every product form submit (bubble phase,
// does NOT prevent default, just observes)
// ---------------------------------------------------------------------
document.addEventListener(
  'submit',
  async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;

    const handle = form.getAttribute('data-product-handle');
    console.log('[togu-tiered-cart] product form submit intercepted, data-product-handle =', handle);

    if (!handle) return; // no handle info, let native behavior happen

    // Prevent full-page navigation to /cart — add via fetch instead, then
    // open the drawer, matching the same UX as the collection-page cards.
    event.preventDefault();
    event.stopPropagation();

    const variantIdInput = form.querySelector('input[name="id"]');
    const variantId = variantIdInput ? variantIdInput.value : null;
    if (!variantId) {
      console.error('[togu-tiered-cart] no variant id found on form, cannot add to cart');
      return;
    }

    try {
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          sections: 'cart-items,cart-footer,cart-item-count,cart-live-region',
          sections_url: window.location.pathname,
        }),
      });

      if (!addRes.ok) throw new Error('Add to cart failed');
      const data = await addRes.json();

      try {
        Alpine.store('cartCount').count = data.item_count;
      } catch (e) {}

      // Dispatch the theme's own real add-to-cart event with rendered
      // sections, so CartItems/CartFooter/cart count morph correctly.
      window.dispatchEvent(
        new CustomEvent('baseline:cart:afteradditem', {
          detail: { response: data, sourceId: form.id || null },
        })
      );

      setTimeout(() => {
        try {
          Alpine.store('modals').open('cart');
        } catch (e) {}
      }, 150);

      // Now run the tier-exclusivity check on top of the freshly added item
      if (findGroupForHandle(handle)) {
        waitForCartChangeThenEnforce(data.item_count - 1);
      }
    } catch (e) {
      console.error('[togu-tiered-cart] product form add-to-cart failed', e);
    }
  },
  true // capture phase — runs before the theme's own compiled Alpine handler
);

// ---------------------------------------------------------------------
// Second listener — for collection-page product cards (togu-product-card.liquid),
// which use a plain <button type="button" class="togu-add" data-product-handle="...">
// instead of a form submit. We only observe the click (don't intercept the
// actual add-to-cart, which happens via togu-card.js) and then run the same
// poll-and-cleanup flow.
// ---------------------------------------------------------------------
document.addEventListener(
  'click',
  async (event) => {
    const addButton = event.target.closest('.togu-add[data-product-handle]');
    if (!addButton) return;

    const handle = addButton.getAttribute('data-product-handle');
    console.log('[togu-tiered-cart] .togu-add clicked (capture), data-product-handle =', handle);

    if (!handle || !findGroupForHandle(handle)) {
      console.log('[togu-tiered-cart] .togu-add handle not tiered, ignoring:', handle);
      return;
    }

    try {
      const cartBefore = await getCart();
      console.log('[togu-tiered-cart] cart before .togu-add click, item_count =', cartBefore.item_count);
      waitForCartChangeThenEnforce(cartBefore.item_count);
    } catch (e) {
      console.error('togu tiered-cart .togu-add pre-check failed', e);
    }
  },
  true // capture phase — runs before togu-card.js's own click handler, which calls stopPropagation()
);